import { useState } from 'react';

import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Text } from '@/design-system/components/ui';
import { useIsOverflowing } from '@/design-system/hooks';
import { cn } from '@/design-system/lib/utils';

const LINE_CLAMP_CLASS: Record<number, string> = {
  1: 'tw:line-clamp-1',
  2: 'tw:line-clamp-2',
  3: 'tw:line-clamp-3',
  4: 'tw:line-clamp-4',
  5: 'tw:line-clamp-5',
  6: 'tw:line-clamp-6',
};

export interface ExpandableTextProps {
  /** Used as the title of the "Read more" dialog. */
  label: string;
  /** The (potentially long) text to clamp. */
  text: string;
  /** Number of lines to clamp to before showing "Read more". Defaults to 2. */
  lines?: number;
  /**
   * Optional identifying context rendered under the dialog title (e.g. who/when),
   * shown only when non-empty. Kept generic — callers format the values.
   */
  details?: { label: string; value: React.ReactNode }[];
  className?: string;
}

// stopPropagation on the interactive children keeps clicks from toggling row selection in clickable table rows.
export const ExpandableText = ({ label, text, lines = 2, details, className }: ExpandableTextProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { ref, isOverflowing } = useIsOverflowing<HTMLParagraphElement>('vertical', [text, lines]);

  const clampClass = LINE_CLAMP_CLASS[lines] ?? LINE_CLAMP_CLASS[2];

  return (
    <div className={cn('tw:flex tw:max-w-[320px] tw:flex-col tw:items-start tw:gap-0.5', className)}>
      <Text
        ref={ref as React.Ref<HTMLElement>}
        as="p"
        variant="body-md"
        className={cn(clampClass, 'tw:max-w-[320px] tw:min-w-0 tw:whitespace-pre-wrap tw:break-words')}
      >
        {text}
      </Text>
      {isOverflowing && (
        <Button
          variant="link"
          size="sm"
          className="tw:h-auto tw:p-0"
          onClick={(e) => {
            e.stopPropagation();
            setIsDialogOpen(true);
          }}
        >
          Read more
        </Button>
      )}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent size="lg" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            {details && details.length > 0 && (
              <div className="tw:mt-1 tw:flex tw:flex-col tw:gap-0.5">
                {details.map((d) => (
                  <Text key={d.label} variant="body-sm" color="muted">
                    <span className="tw:font-medium">{d.label}:</span> {d.value}
                  </Text>
                ))}
              </div>
            )}
          </DialogHeader>
          <div className="tw:max-h-[60vh] tw:overflow-y-auto tw:px-6 tw:pb-6">
            <Text variant="body-md" className="tw:whitespace-pre-wrap tw:break-words">
              {text}
            </Text>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

ExpandableText.displayName = 'ExpandableText';
